import React, { useState } from "react";
import { Link } from "react-router-dom";

import PageContent from "components/PageContent";
import AvailabilitySection from "components/PageDashboard/AvailabilitySection";
import ExternalCommitmentsSection from "components/PageDashboard/ExternalCommitmentsSection";
import SessionTypesSection from "components/PageDashboard/SessionTypesSection";
import SingleSessionPricesSection from "components/PageDashboard/SingleSessionPricesSection";
import ContactsAndMessagesSection from "components/PageDashboard/ContactsAndMessagesSection";
import MeetingRequestsSection from "components/PageDashboard/MeetingRequestsSection";
import MeetingsSection from "components/PageDashboard/MeetingsSection";
import AccountSettingsSection from "components/PageDashboard/AccountSettingsSection";
import ProfilePhotoSection from "components/PageDashboard/ProfilePhotoSection";
import ICalFeedsToImportSection from "components/PageDashboard/ICalFeedsToImportSection";
import DeleteAccountSection from "components/PageDashboard/DeleteAccountSection";
import ProfileUrlSection from "components/PageDashboard/ProfileUrlSection";
import ProfileVideoSection from "components/PageDashboard/ProfileVideoSection";
import SkypeIdSection from "components/PageDashboard/SkypeIdSection";
import DefaultGoogleMeetLinkSection from "components/PageDashboard/DefaultGoogleMeetLinkSection";
import ICalSection from "components/PageDashboard/ICalSection";
import AlertBox from "components/AlertBox";
import SectionBox from "components/SectionBox";
import { usePageTitlePrefix } from "hooks/usePageTitlePrefix";
import { useAccountStatus } from "hooks/useAccountStatus";
import terminology from "terminology.json";
import { ucFirst } from "utils/ucFirst";

import NarrowSection from "./NarrowSection";
import WideSection from "./WideSection";
import SectionGroup from "./SectionGroup";
import MentorsOnly from "./MentorsOnly";
import MenteesOnly from "./MenteesOnly";

interface DashboardContextState {
	meetingsSectionRefreshNum: number;
	sessionTypesRefreshNum: number;

	haveSessionTypes: boolean | undefined;
	haveSingleSessionPrices: boolean | undefined;
}

export const DashboardContext = React.createContext<
	[
		DashboardContextState,
		React.Dispatch<React.SetStateAction<DashboardContextState>>
	]
>([
	{
		meetingsSectionRefreshNum: 1,
		sessionTypesRefreshNum: 1,
		haveSessionTypes: undefined,
		haveSingleSessionPrices: undefined
	},
	arg => undefined
]);

const PageDashboard: React.FunctionComponent = () => {
	usePageTitlePrefix("Dashboard");
	const { isMentor } = useAccountStatus();

	const [dashboardContextState, setDashboardContextState] = useState<
		DashboardContextState
	>({
		meetingsSectionRefreshNum: new Date().getTime(),
		sessionTypesRefreshNum: new Date().getTime(),
		haveSessionTypes: undefined,
		haveSingleSessionPrices: undefined
	});

	const needsToAddSessionTypes =
		isMentor && dashboardContextState.haveSessionTypes === false;
	const needsToAddSingleSessionPrices =
		isMentor && dashboardContextState.haveSingleSessionPrices === false;

	// Contacts and Messages section collapsed-on-default state should be true for non-mentors and false for mentors.
	// The logic of this doesn't work if isMentor is allowed to be undefined while it is loading.  So, wait until
	// it has loaded before rendering this page.
	if (isMentor === undefined) {
		return null;
	}

	return (
		<DashboardContext.Provider
			value={[dashboardContextState, setDashboardContextState]}
		>
			<PageContent type="admin">
				{needsToAddSessionTypes || needsToAddSingleSessionPrices ? (
					<AlertBox
						allowDismiss={false}
						contents={[
							...(needsToAddSessionTypes
								? [
										<>
											Until you add at least one{" "}
											<a href="#session-types-section">session type</a> your
											profile will show you as unavailable.
										</>
								  ]
								: []),
							...(needsToAddSingleSessionPrices
								? [
										<>
											You need to add some{" "}
											<a href="#session-types-section">prices</a> to go with
											your sessions.
										</>
								  ]
								: [])
						]}
					/>
				) : null}

				<MenteesOnly>
					<WideSection
						title={`${ucFirst(terminology.mentors)}, contacts and messages`}
						isCollapsedByDefault={false}
					>
						<ContactsAndMessagesSection />
					</WideSection>
					<WideSection
						title={`${terminology.mentor} directory`}
						isCollapsedByDefault={false}
					>
						<p>
							If you need to find a {terminology.mentor}, you can use our{" "}
							<Link to={`/${terminology.mentorsInUrls}`}>
								{terminology.mentor} directory
							</Link>
						</p>
					</WideSection>
				</MenteesOnly>

				<MentorsOnly>
					<WideSection
						title={terminology.meetingRequests}
						isCollapsedByDefault={false}
					>
						<MeetingRequestsSection />
					</WideSection>
				</MentorsOnly>

				<WideSection
					title={"Current " + terminology.meetings}
					isCollapsedByDefault={false}
				>
					<MeetingsSection />
				</WideSection>

				<MentorsOnly>
					<SectionGroup title="Relationships">
						<WideSection title="Contacts and messages" isCollapsedByDefault>
							<ContactsAndMessagesSection />
						</WideSection>
						<WideSection
							title={`${terminology.mentor} directory`}
							isCollapsedByDefault
						>
							<p>
								If you want to {terminology.learn} as well as{" "}
								{terminology.teach}, you find a {terminology.mentor} using our{" "}
								<Link to={`/${terminology.mentorsInUrls}`}>
									{terminology.mentor} directory
								</Link>
							</p>
						</WideSection>
					</SectionGroup>
				</MentorsOnly>

				<MentorsOnly>
					<SectionGroup title={`Availability`}>
						<WideSection title={`General availability`}>
							<AvailabilitySection />
						</WideSection>
						<WideSection title="External commitments">
							<ExternalCommitmentsSection />
						</WideSection>
					</SectionGroup>
					<SectionGroup title="Lessons and prices">
						<WideSection
							title={`${terminology.meeting} types`}
							anchorName="session-types-section"
						>
							<SessionTypesSection />
						</WideSection>
						<WideSection
							title={`Single ${terminology.meeting} prices and durations`}
							anchorName="session-prices-section"
						>
							<SingleSessionPricesSection />
						</WideSection>
					</SectionGroup>
					<SectionGroup title="ICal">
						<WideSection
							title="ICal import"
							anchorName="iCal-feeds-to-import-section"
						>
							<ICalFeedsToImportSection />
						</WideSection>
						<WideSection title="ICal export">
							<ICalSection />
						</WideSection>
					</SectionGroup>
				</MentorsOnly>

				<MenteesOnly>
					<WideSection title="ICal export">
						<ICalSection />
					</WideSection>
					<WideSection title="Basic data and account settings">
						<AccountSettingsSection />
					</WideSection>
				</MenteesOnly>

				<MentorsOnly>
					<SectionGroup title="Profile">
						<WideSection title="Basic data and account settings">
							<AccountSettingsSection />
						</WideSection>
						<div className="row gtr-uniform">
							<NarrowSection title="Photo">
								<ProfilePhotoSection />
							</NarrowSection>
							<NarrowSection title="Video">
								<SectionBox>
									<ProfileVideoSection />
								</SectionBox>
							</NarrowSection>
						</div>
						<WideSection title="URL">
							<SectionBox>
								<ProfileUrlSection />
							</SectionBox>
						</WideSection>
					</SectionGroup>
					<SectionGroup title="Video chat platforms">
						<div className="row gtr-uniform">
							<NarrowSection title="Skype ID">
								<SectionBox>
									<SkypeIdSection />
								</SectionBox>
							</NarrowSection>
							<NarrowSection title="Default Google Meet link">
								<SectionBox>
									<DefaultGoogleMeetLinkSection />
								</SectionBox>
							</NarrowSection>
						</div>
					</SectionGroup>
				</MentorsOnly>

				<MenteesOnly>
					<WideSection title="Skype ID">
						<SectionBox>
							<SkypeIdSection />
						</SectionBox>
					</WideSection>
					<WideSection
						title="Teacher account"
						anchorName="teacher-account-section"
						isCollapsedByDefault={false}
					>
						<SectionBox>
							<p>
								If you would to apply to upgrade your account to a{" "}
								{terminology.mentor} account, so that you can use the{" "}
								{terminology.mentor}-specific features, please send an email to{" "}
								<a href="mailto:support@aldeia.world">support@aldeia.world</a>.
							</p>
						</SectionBox>
					</WideSection>
				</MenteesOnly>

				<WideSection title="Delete account" anchorName="delete-account-section">
					<SectionBox>
						<DeleteAccountSection />
					</SectionBox>
				</WideSection>
			</PageContent>
		</DashboardContext.Provider>
	);
};

export default PageDashboard;
